import React, { useState, useEffect, useMemo } from 'react';
import { Save, X, Calendar, Plus, Trash, AlertCircle, History } from 'lucide-react';
import { toast } from 'react-toastify';
import authUtils from '../utils/authUtils';

// Utility function để generate unique ID
const generateUniqueId = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let result = '';
    const timeBase = Date.now().toString(36).toUpperCase().slice(-4);
    result += timeBase.padStart(4, chars.charAt(Math.floor(Math.random() * chars.length)));
    for (let i = 0; i < 4; i++) {
        result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
};

// Parse number safely
const parseNumber = (value) => {
    if (value === null || value === undefined || value === '') return 0;
    const num = parseInt(value, 10);
    return isNaN(num) ? 0 : num;
};

// Format number with thousand separator
const formatNumber = (number) => {
    if (number === null || number === undefined || number === '') return '0';
    const num = parseInt(number, 10);
    if (isNaN(num)) return '0';
    return num.toLocaleString('vi-VN');
};

// Format date to YYYY-MM-DD
const formatDateForInput = (date) => {
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const HieuChinhTonBBE = ({ onClose, onSuccess, tonBBEHienTai = 0, hieuChinhData = [] }) => {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [hieuChinhRows, setHieuChinhRows] = useState([]);

    // Tính tổng hiệu chỉnh hiện có
    const tongHieuChinhHienCo = hieuChinhData.reduce((sum, hc) => sum + parseNumber(hc['SỐ LƯỢNG HIỆU CHỈNH']), 0);

    // Sắp xếp lịch sử theo ngày mới nhất trước
    const sortedHieuChinhData = useMemo(() => {
        return [...hieuChinhData].sort((a, b) => {
            const dateA = new Date(a['NGÀY HIỆU CHỈNH']);
            const dateB = new Date(b['NGÀY HIỆU CHỈNH']);
            return dateB - dateA; // Ngày lớn nhất (mới nhất) ở trên
        });
    }, [hieuChinhData]);

    // Khởi tạo với 1 dòng
    useEffect(() => {
        setHieuChinhRows([createEmptyRow()]);
    }, []);

    // Tạo dòng rỗng
    const createEmptyRow = () => ({
        tempId: generateUniqueId(),
        ngayHieuChinh: formatDateForInput(new Date()),
        soLuongHieuChinh: '',
        ghiChu: ''
    });

    // Thêm dòng mới
    const handleAddRow = () => {
        setHieuChinhRows(prev => [...prev, createEmptyRow()]);
    };

    // Xóa dòng
    const handleRemoveRow = (index) => {
        if (hieuChinhRows.length === 1) {
            toast.warning('Phải có ít nhất một dòng');
            return;
        }
        setHieuChinhRows(prev => prev.filter((_, i) => i !== index));
    };

    // Cập nhật giá trị dòng
    const handleUpdateRow = (index, field, value) => {
        setHieuChinhRows(prev =>
            prev.map((row, i) =>
                i === index ? { ...row, [field]: value } : row
            )
        );
    };

    // Tính tổng hiệu chỉnh mới nhập
    const tongHieuChinhMoi = hieuChinhRows.reduce((sum, row) => sum + parseNumber(row.soLuongHieuChinh), 0);

    // Tồn BBE hiện tại (đã bao gồm hiệu chỉnh cũ) = tồn gốc + hiệu chỉnh cũ
    const tonBBEDaBaoGomHieuChinh = tonBBEHienTai + tongHieuChinhHienCo;

    // Tồn BBE sau hiệu chỉnh mới = tồn hiện tại (đã bao gồm HC cũ) + hiệu chỉnh mới
    const tonBBESauHieuChinh = tonBBEDaBaoGomHieuChinh + tongHieuChinhMoi;

    // Lưu dữ liệu
    const handleSave = async () => {
        if (isSubmitting) return;

        // Lọc các dòng có dữ liệu
        const validRows = hieuChinhRows.filter(row => parseNumber(row.soLuongHieuChinh) !== 0);

        if (validRows.length === 0) {
            toast.error('Vui lòng nhập số lượng hiệu chỉnh cho ít nhất một dòng');
            return;
        }

        // Kiểm tra ngày
        const invalidDateRow = validRows.find(row => !row.ngayHieuChinh);
        if (invalidDateRow) {
            toast.error('Vui lòng chọn ngày hiệu chỉnh cho tất cả các dòng');
            return;
        }

        try {
            setIsSubmitting(true);

            const records = validRows.map(row => ({
                ID_HC: generateUniqueId(),
                'NGÀY HIỆU CHỈNH': row.ngayHieuChinh,
                'SỐ LƯỢNG HIỆU CHỈNH': parseNumber(row.soLuongHieuChinh),
                'GHI CHÚ': (row.ghiChu || '').trim()
            }));

            await authUtils.apiRequest('HIEU_CHINH_TON_BBE', 'Add', { Rows: records });

            toast.success(`Đã lưu ${records.length} bản ghi hiệu chỉnh tồn BBE thành công!`);

            // Reset form sau khi lưu thành công
            setHieuChinhRows([createEmptyRow()]);

            // Gọi onSuccess để refresh data nhưng KHÔNG đóng modal
            onSuccess?.();

        } catch (error) {
            console.error('Error saving hieu chinh data:', error);
            toast.error('Lỗi khi lưu dữ liệu hiệu chỉnh: ' + (error.message || 'Không xác định'));
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
                {/* Header */}
                <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-4 flex items-center justify-between">
                    <h2 className="text-xl font-bold text-white flex items-center gap-2">
                        <AlertCircle className="w-6 h-6" />
                        Hiệu chỉnh tồn Bao bì em
                    </h2>
                    <button
                        onClick={onClose}
                        className="text-white hover:bg-white/20 rounded-lg p-2 transition-colors"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto p-6">
                    {/* Thông tin tồn kho hiện tại */}
                    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4 mb-6">
                        <div className="grid grid-cols-3 gap-4 text-center">
                            <div>
                                <p className="text-sm text-gray-600 mb-1">Tồn BBE hiện tại</p>
                                <p className={`text-xl font-bold ${tonBBEDaBaoGomHieuChinh >= 0 ? 'text-blue-600' : 'text-red-600'}`}>{formatNumber(tonBBEDaBaoGomHieuChinh)} KG</p>
                            </div>
                            <div>
                                <p className="text-sm text-gray-600 mb-1">Hiệu chỉnh mới</p>
                                <p className={`text-xl font-bold ${tongHieuChinhMoi >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                    {tongHieuChinhMoi >= 0 ? '+' : ''}{formatNumber(tongHieuChinhMoi)} KG
                                </p>
                            </div>
                            <div>
                                <p className="text-sm text-gray-600 mb-1">Tồn BBE sau hiệu chỉnh</p>
                                <p className={`text-xl font-bold ${tonBBESauHieuChinh >= 0 ? 'text-purple-600' : 'text-red-600'}`}>{formatNumber(tonBBESauHieuChinh)} KG</p>
                            </div>
                        </div>
                    </div>

                    {/* Form nhập hiệu chỉnh - ĐẶT Ở TRÊN */}
                    <div className="space-y-4 mb-6">
                        <div className="flex items-center justify-between mb-2">
                            <h3 className="text-sm font-semibold text-gray-700">Nhập hiệu chỉnh mới</h3>
                            <button
                                type="button"
                                onClick={handleAddRow}
                                className="flex items-center gap-1 px-3 py-1.5 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700 transition-colors"
                            >
                                <Plus className="w-4 h-4" />
                                Thêm dòng
                            </button>
                        </div>

                        <div className="space-y-3">
                            {hieuChinhRows.map((row, index) => (
                                <div key={row.tempId} className="flex items-center gap-3 bg-gray-50 p-3 rounded-lg">
                                    <div className="w-8 text-center text-sm font-medium text-gray-500">
                                        {index + 1}
                                    </div>

                                    <div className="flex-1">
                                        <label className="block text-xs text-gray-600 mb-1">Ngày hiệu chỉnh</label>
                                        <div className="relative">
                                            <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                                            <input
                                                type="date"
                                                value={row.ngayHieuChinh}
                                                onChange={(e) => handleUpdateRow(index, 'ngayHieuChinh', e.target.value)}
                                                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                                            />
                                        </div>
                                    </div>

                                    <div className="flex-1">
                                        <label className="block text-xs text-gray-600 mb-1">Số lượng hiệu chỉnh (KG)</label>
                                        <input
                                            type="number"
                                            placeholder="Nhập số dương (+) hoặc âm (-)"
                                            value={row.soLuongHieuChinh}
                                            onChange={(e) => handleUpdateRow(index, 'soLuongHieuChinh', e.target.value)}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                                        />
                                    </div>

                                    <div className="flex-1">
                                        <label className="block text-xs text-gray-600 mb-1">Ghi chú</label>
                                        <input
                                            type="text"
                                            placeholder="Ghi chú (tùy chọn)"
                                            value={row.ghiChu}
                                            onChange={(e) => handleUpdateRow(index, 'ghiChu', e.target.value)}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                                        />
                                    </div>

                                    <button
                                        type="button"
                                        onClick={() => handleRemoveRow(index)}
                                        className="mt-5 p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                                        title="Xóa dòng"
                                    >
                                        <Trash className="w-4 h-4" />
                                    </button>
                                </div>
                            ))}
                        </div>

                        {/* Ghi chú hướng dẫn */}
                        <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                            <p className="text-sm text-yellow-800">
                                <strong>Hướng dẫn:</strong> Nhập số dương (+) để tăng tồn kho, số âm (-) để giảm tồn kho.
                                Ví dụ: Nếu tồn thực tế nhiều hơn hệ thống 100 KG, nhập <code className="bg-yellow-100 px-1 rounded">100</code>.
                                Nếu tồn thực tế ít hơn hệ thống 50 KG, nhập <code className="bg-yellow-100 px-1 rounded">-50</code>.
                            </p>
                        </div>
                    </div>

                    {/* Lịch sử hiệu chỉnh - ĐẶT Ở DƯỚI */}
                    {hieuChinhData.length > 0 && (
                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                            <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                                <History className="w-4 h-4" />
                                Lịch sử hiệu chỉnh ({hieuChinhData.length} bản ghi)
                            </h3>
                            {/* Tăng max-h để hiển thị được 10 dòng */}
                            <div className="max-h-80 overflow-y-auto">
                                <table className="w-full text-sm">
                                    <thead className="bg-gray-100 sticky top-0">
                                        <tr>
                                            <th className="px-3 py-2 text-left">Ngày</th>
                                            <th className="px-3 py-2 text-right">Số lượng</th>
                                            <th className="px-3 py-2 text-left">Ghi chú</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {sortedHieuChinhData.map((hc, idx) => (
                                            <tr key={hc.ID_HC || idx} className="border-t border-gray-200 hover:bg-gray-100">
                                                <td className="px-3 py-2">
                                                    {new Date(hc['NGÀY HIỆU CHỈNH']).toLocaleDateString('vi-VN')}
                                                </td>
                                                <td className={`px-3 py-2 text-right font-medium ${parseNumber(hc['SỐ LƯỢNG HIỆU CHỈNH']) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                                    {parseNumber(hc['SỐ LƯỢNG HIỆU CHỈNH']) >= 0 ? '+' : ''}
                                                    {formatNumber(hc['SỐ LƯỢNG HIỆU CHỈNH'])} KG
                                                </td>
                                                <td className="px-3 py-2 text-gray-600">{hc['GHI CHÚ'] || '-'}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                    <tfoot className="bg-gray-100 font-semibold sticky bottom-0">
                                        <tr>
                                            <td className="px-3 py-2">Tổng cộng</td>
                                            <td className={`px-3 py-2 text-right ${tongHieuChinhHienCo >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                                {tongHieuChinhHienCo >= 0 ? '+' : ''}{formatNumber(tongHieuChinhHienCo)} KG
                                            </td>
                                            <td></td>
                                        </tr>
                                    </tfoot>
                                </table>
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="border-t border-gray-200 px-6 py-4 bg-gray-50 flex justify-end gap-3">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors"
                    >
                        Đóng
                    </button>
                    <button
                        type="button"
                        onClick={handleSave}
                        disabled={isSubmitting}
                        className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center gap-2 disabled:opacity-50"
                    >
                        <Save className="w-4 h-4" />
                        {isSubmitting ? 'Đang lưu...' : 'Lưu hiệu chỉnh'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default HieuChinhTonBBE;
